import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCalendar, FaUser, FaArrowRight } from 'react-icons/fa';
import toast from 'react-hot-toast'; // Assuming you have react-hot-toast installed

const blogPosts = [
  {
    title: 'Introducing AutoWave Pro: The Next Generation AI Agent Platform',
    excerpt: 'Discover AutoWave Pro, our revolutionary AI agent platform that combines web browsing, code editing, document creation, SEO optimization, and advanced data analysis in one powerful solution.',
    author: 'Botrex Team',
    date: 'July 2, 2025',
    image: '/images/blog/ai pics 21.jpeg',
    category: 'Product Launch',
    link: 'https://www.autowave.pro'
  },
  {
    title: 'The Future of AI in Business',
    excerpt: 'Discover how artificial intelligence is reshaping the business landscape and what it means for your organization.',
    author: 'Bernard Marr',
    date: 'March 15, 2024',
    image: '/images/blog/ai-future.jpg',
    category: 'AI Trends',
    link: 'https://www.forbes.com/sites/bernardmarr/2024/01/02/the-10-biggest-business-trends-for-2024/'
  },
  {
    title: 'Machine Learning Best Practices',
    excerpt: 'Learn the essential best practices for implementing machine learning solutions in your business.',
    author: 'Andrew Ng',
    date: 'March 12, 2024',
    image: '/images/blog/ml-practices.jpg',
    category: 'Machine Learning',
    link: 'https://www.coursera.org/learn/machine-learning'
  },
  {
    title: 'AI Integration Success Stories',
    excerpt: 'Real-world examples of successful AI integration projects and their impact on business outcomes.',
    author: 'Kai-Fu Lee',
    date: 'March 10, 2024',
    image: '/images/blog/ai-success.jpg',
    category: 'Case Studies',
    link: 'https://www.forbes.com/sites/gilpress/2023/12/27/ai-in-2024-predictions-from-ai-experts/'
  },
  {
    title: 'The Role of NLP in Modern Business',
    excerpt: 'Understanding how Natural Language Processing is transforming customer service and communication.',
    author: 'Sebastian Ruder',
    date: 'March 8, 2024',
    image: '/images/blog/nlp-business.jpg',
    category: 'NLP',
    link: 'https://www.kdnuggets.com/2023/12/natural-language-processing-trends-2024'
  },
  {
    title: 'AI Security Considerations',
    excerpt: 'Essential security considerations when implementing AI solutions in your organization.',
    author: 'Bruce Schneier',
    date: 'March 5, 2024',
    image: '/images/blog/ai-security.jpg',
    category: 'Security',
    link: 'https://www.technologyreview.com/2024/01/03/1085416/cybersecurity-predictions-2024/'
  },
  {
    title: 'Scaling AI Solutions',
    excerpt: 'Strategies for effectively scaling AI solutions across your organization.',
    author: 'Sam Altman',
    date: 'March 1, 2024',
    image: '/images/blog/ai-scaling.jpg',
    category: 'Strategy',
    link: 'https://hbr.org/2024/01/how-to-build-an-ai-first-organization'
  }
];

const Blog = () => {
  const [showSubscribe, setShowSubscribe] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    // Here you would typically handle the email subscription
    toast.success('Thank you for subscribing! You will receive our latest updates.');
  };

  return (
    <div className="min-h-screen bg-nvidia-dark py-20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Latest <span className="text-nvidia-green">Insights</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Stay updated with the latest trends, insights, and best practices in AI and machine learning.
          </p>
          <div className="max-w-5xl mx-auto mt-12 mb-16 relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-nvidia-green/40 to-nvidia-dark/50 rounded-lg mix-blend-overlay"></div>
            <div className="absolute inset-0 bg-nvidia-green/15 rounded-lg mix-blend-color"></div>
            <img
              src="/images/blog/ai pics 21.jpeg"
              alt="Latest AI Insights and Trends"
              className="w-full h-auto rounded-lg shadow-2xl hover:shadow-3xl transition-shadow duration-300 relative z-0"
              style={{
                imageRendering: 'crisp-edges',
                WebkitFontSmoothing: 'antialiased',
                objectFit: 'cover',
                filter: 'contrast(1.2) saturate(1.15) brightness(1.1) sharpen(2.5) hue-rotate(-10deg)',
                transform: 'translateZ(0) scale(1.002)',
                backfaceVisibility: 'hidden',
                maxHeight: '600px',
                WebkitImageRendering: '-webkit-optimize-contrast',
                MozImageRendering: 'crisp-edges',
                WebkitFilter: 'contrast(1.2) brightness(1.1) hue-rotate(-10deg)',
                willChange: 'transform'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-nvidia-green/30 to-nvidia-dark/40 rounded-lg mix-blend-color-dodge"></div>
            <div className="absolute inset-0 bg-gradient-to-tr from-nvidia-green/20 to-transparent rounded-lg mix-blend-overlay"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post, index) => (
            <article
              key={index}
              className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-nvidia-green/40 transition-colors shadow-sm hover:shadow-md"
            >
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                  <span className="flex items-center">
                    <FaCalendar className="mr-2" />
                    {post.date}
                  </span>
                  <span className="flex items-center">
                    <FaUser className="mr-2" />
                    {post.author}
                  </span>
                </div>
                <span className="text-nvidia-green text-sm font-medium">
                  {post.category}
                </span>
                <h2 className="text-gray-900 text-xl font-bold mt-2 mb-4">{post.title}</h2>
                <p className="text-gray-600 mb-4">{post.excerpt}</p>
                <Link
                  to={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-nvidia-green hover:text-nvidia-green-600 transition-colors"
                >
                  Learn More <FaArrowRight className="ml-2" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12 text-center">
          <button 
            onClick={() => setShowSubscribe(true)}
            className="bg-nvidia-green text-black px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
          >
            Load More
          </button>
        </div>

        {showSubscribe && (
          <div className="mt-16 max-w-xl mx-auto bg-white rounded-lg p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Subscribe to Our Newsletter
            </h3>
            <p className="text-gray-600 mb-6">
              Get the latest insights and updates about AI technology delivered directly to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-4">
              <div>
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-nvidia-green focus:ring-1 focus:ring-nvidia-green outline-none transition-colors"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-nvidia-green text-black px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
              >
                Subscribe Now
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
